import { type Client } from '@microsoft/microsoft-graph-client';
import type {
  IntegrationProvider,
  ExternalUserSeat,
  IntegrationSyncResult,
} from '@/models/integrations';
import type { Employee, Application, AppAssignment } from '@/models/types';
import {
  getListItems,
  createListItem,
  updateListItem,
} from './sharePointService';
import { logChange } from './snapshotService';

// ─── Provider-specific seat fetchers ─────────────────────────────────────────

/**
 * Azure DevOps: returns users with their exact license tier.
 * `seatType` is the ADO `licenseDisplayName` — e.g., "Basic", "Basic + Test Plans",
 * "Visual Studio Enterprise", "Stakeholder" — this is what we match to OC_Applications rows.
 */
async function fetchAdoSeats(
  apiKey: string,
  organization: string,
): Promise<ExternalUserSeat[]> {
  const url = `https://vsaex.dev.azure.com/${organization}/_apis/userentitlements?api-version=7.1-preview.4&top=500`;
  const resp = await fetch(url, {
    headers: {
      Authorization: `Basic ${btoa(':' + apiKey)}`,
      Accept: 'application/json',
    },
  });
  if (!resp.ok) throw new Error(`ADO returned ${resp.status}: ${await resp.text()}`);
  const data = await resp.json();

  return (data.members ?? data.items ?? data.value ?? []).map(
    (m: {
      user: { principalName: string; displayName: string; mailAddress?: string };
      accessLevel: { licenseDisplayName: string; licensingSource: string };
      dateCreated?: string;
    }) => ({
      email: (m.user.mailAddress || m.user.principalName || '').toLowerCase(),
      name: m.user.displayName,
      seatType: m.accessLevel.licenseDisplayName, // "Basic", "VS Enterprise", etc.
      role: m.accessLevel.licensingSource,
      licensedAt: m.dateCreated,
    }),
  );
}

/** GitHub Copilot */
async function fetchGitHubCopilotSeats(
  apiKey: string,
  org: string,
): Promise<ExternalUserSeat[]> {
  const url = `https://api.github.com/orgs/${org}/copilot/billing/seats?per_page=100`;
  const resp = await fetch(url, {
    headers: {
      Authorization: `Bearer ${apiKey}`,
      Accept: 'application/vnd.github+json',
      'X-GitHub-Api-Version': '2022-11-28',
    },
  });
  if (!resp.ok) throw new Error(`GitHub returned ${resp.status}`);
  const data = await resp.json();
  return (data.seats ?? []).map(
    (s: {
      assignee: { login: string; email?: string; name?: string };
      created_at: string;
    }) => ({
      email: (s.assignee.email ?? `${s.assignee.login}@github`).toLowerCase(),
      name: s.assignee.name ?? s.assignee.login,
      seatType: 'Copilot',
      licensedAt: s.created_at,
    }),
  );
}

/** Figma */
async function fetchFigmaSeats(
  apiKey: string,
  teamId: string,
): Promise<ExternalUserSeat[]> {
  const url = `https://api.figma.com/v1/teams/${teamId}/members`;
  const resp = await fetch(url, { headers: { 'X-Figma-Token': apiKey } });
  if (!resp.ok) throw new Error(`Figma returned ${resp.status}`);
  const data = await resp.json();
  return (data.members ?? []).map(
    (m: { email: string; name: string; permission: string }) => ({
      email: m.email.toLowerCase(),
      name: m.name,
      seatType: m.permission,
    }),
  );
}

/** Slack */
async function fetchSlackSeats(apiKey: string): Promise<ExternalUserSeat[]> {
  const resp = await fetch('https://slack.com/api/users.list?limit=1000', {
    headers: { Authorization: `Bearer ${apiKey}` },
  });
  if (!resp.ok) throw new Error(`Slack returned ${resp.status}`);
  const data = await resp.json();
  if (!data.ok) throw new Error(`Slack error: ${data.error}`);
  return (data.members ?? [])
    .filter(
      (m: { deleted: boolean; is_bot: boolean; profile: { email?: string } }) =>
        !m.deleted && !m.is_bot && m.profile.email,
    )
    .map(
      (m: {
        profile: { email: string; real_name: string };
        is_admin: boolean;
      }) => ({
        email: m.profile.email.toLowerCase(),
        name: m.profile.real_name,
        seatType: m.is_admin ? 'Admin' : 'Member',
      }),
    );
}

/** Linear */
async function fetchLinearSeats(apiKey: string): Promise<ExternalUserSeat[]> {
  const resp = await fetch('https://api.linear.app/graphql', {
    method: 'POST',
    headers: { Authorization: apiKey, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      query: `query { users(first: 500) { nodes { email name active admin } } }`,
    }),
  });
  if (!resp.ok) throw new Error(`Linear returned ${resp.status}`);
  const data = await resp.json();
  return (data.data?.users?.nodes ?? [])
    .filter((u: { active: boolean }) => u.active)
    .map((u: { email: string; name: string; admin: boolean }) => ({
      email: u.email.toLowerCase(),
      name: u.name,
      seatType: u.admin ? 'Admin' : 'Member',
    }));
}

/** Claude (Anthropic) Admin API */
async function fetchClaudeSeats(
  apiKey: string,
  orgId: string,
): Promise<ExternalUserSeat[]> {
  const url = `https://api.anthropic.com/v1/organizations/${orgId}/users?limit=1000`;
  const resp = await fetch(url, {
    headers: {
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01',
    },
  });
  if (!resp.ok) throw new Error(`Anthropic returned ${resp.status}`);
  const data = await resp.json();
  return (data.data ?? []).map(
    (u: { email: string; name?: string; role: string; added_at?: string }) => ({
      email: u.email.toLowerCase(),
      name: u.name ?? u.email,
      seatType: u.role, // "admin", "developer", "billing"
      licensedAt: u.added_at,
    }),
  );
}

/** Snyk */
async function fetchSnykSeats(
  apiKey: string,
  orgId: string,
): Promise<ExternalUserSeat[]> {
  const url = `https://api.snyk.io/rest/orgs/${orgId}/users?version=2024-10-15&limit=100`;
  const resp = await fetch(url, {
    headers: {
      Authorization: `token ${apiKey}`,
      Accept: 'application/vnd.api+json',
    },
  });
  if (!resp.ok) throw new Error(`Snyk returned ${resp.status}`);
  const data = await resp.json();
  return (data.data ?? []).map(
    (u: { attributes: { email: string; name?: string; role?: string } }) => ({
      email: u.attributes.email.toLowerCase(),
      name: u.attributes.name ?? u.attributes.email,
      seatType: u.attributes.role ?? 'Member',
    }),
  );
}

/** SendGrid (Twilio) */
async function fetchSendGridSeats(apiKey: string): Promise<ExternalUserSeat[]> {
  const resp = await fetch('https://api.sendgrid.com/v3/teammates', {
    headers: { Authorization: `Bearer ${apiKey}` },
  });
  if (!resp.ok) throw new Error(`SendGrid returned ${resp.status}`);
  const data = await resp.json();
  return (data.result ?? []).map(
    (t: { email: string; username: string; is_admin: boolean }) => ({
      email: t.email.toLowerCase(),
      name: t.username,
      seatType: t.is_admin ? 'Admin' : 'Teammate',
    }),
  );
}

/**
 * CSV parser
 * Expected: email[,name][,role][,seatType]
 */
export function parseCsvSeats(csvText: string): ExternalUserSeat[] {
  const lines = csvText.trim().split('\n');
  if (lines.length < 2) return [];

  const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
  const emailIdx = headers.indexOf('email');
  const nameIdx = headers.indexOf('name');
  const roleIdx = headers.indexOf('role');
  const seatIdx = headers.indexOf('seattype');

  if (emailIdx < 0) throw new Error('CSV must have an "email" column');

  return lines.slice(1).map((line) => {
    const cols = line.split(',').map((c) => c.trim());
    return {
      email: cols[emailIdx]?.toLowerCase() ?? '',
      name: nameIdx >= 0 ? cols[nameIdx] ?? '' : cols[emailIdx] ?? '',
      role: roleIdx >= 0 ? cols[roleIdx] : undefined,
      seatType: seatIdx >= 0 ? cols[seatIdx] : undefined,
    };
  });
}

// ─── Main sync orchestrator ──────────────────────────────────────────────────

interface SyncConfig {
  apiKey?: string;
  organizationId?: string;
  csvData?: string;
  /**
   * Multi-tier mode: map external tier name → SP Applications item ID.
   * When provided, each user is assigned to the app row matching their tier.
   * If omitted, all users assigned to mappedAppId (default).
   */
  tierMapping?: Record<string, string>;
}

export async function syncExternalIntegration(
  client: Client,
  provider: IntegrationProvider,
  config: SyncConfig,
  defaultMappedAppId: string,
  currentUserEmail: string,
): Promise<IntegrationSyncResult> {
  const apps = await getListItems<Application>(client, 'OC_Applications');
  const defaultApp = apps.find((a) => a.id === defaultMappedAppId);
  const appName = defaultApp?.fields.Title ?? provider;

  try {
    let seats: ExternalUserSeat[] = [];

    switch (provider) {
      case 'AzureDevOps':
        if (!config.apiKey || !config.organizationId)
          throw new Error('API key and organization required');
        seats = await fetchAdoSeats(config.apiKey, config.organizationId);
        break;
      case 'GitHub':
        if (!config.apiKey || !config.organizationId)
          throw new Error('API key and org required');
        seats = await fetchGitHubCopilotSeats(config.apiKey, config.organizationId);
        break;
      case 'Figma':
        if (!config.apiKey || !config.organizationId)
          throw new Error('API key and team ID required');
        seats = await fetchFigmaSeats(config.apiKey, config.organizationId);
        break;
      case 'Slack':
        if (!config.apiKey) throw new Error('API key required');
        seats = await fetchSlackSeats(config.apiKey);
        break;
      case 'Linear':
        if (!config.apiKey) throw new Error('API key required');
        seats = await fetchLinearSeats(config.apiKey);
        break;
      case 'Claude':
        if (!config.apiKey || !config.organizationId)
          throw new Error('Admin API key and organization ID required');
        seats = await fetchClaudeSeats(config.apiKey, config.organizationId);
        break;
      case 'Snyk':
        if (!config.apiKey || !config.organizationId)
          throw new Error('API key and org ID required');
        seats = await fetchSnykSeats(config.apiKey, config.organizationId);
        break;
      case 'SendGrid':
        if (!config.apiKey) throw new Error('API key required');
        seats = await fetchSendGridSeats(config.apiKey);
        break;
      case 'CSV':
      case 'Windsurf':
      case 'Notion':
        if (!config.csvData) throw new Error('CSV data required');
        seats = parseCsvSeats(config.csvData);
        break;
    }

    // Reconcile
    const employees = await getListItems<Employee>(client, 'OC_Employees');
    const empByEmail = new Map<string, string>();
    for (const e of employees) {
      empByEmail.set(e.fields.Email.toLowerCase(), e.id);
    }

    // Get all assignments — we track per (empId, appId) pair for ALL relevant apps
    // When tier mapping is used, multiple appIds are involved
    const relevantAppIds = new Set<number>([Number(defaultMappedAppId)]);
    if (config.tierMapping) {
      for (const id of Object.values(config.tierMapping)) {
        relevantAppIds.add(Number(id));
      }
    }

    const assignments = await getListItems<AppAssignment>(
      client,
      'OC_AppAssignments',
    );
    type Assign = (typeof assignments)[0];
    const existing = new Map<string, Assign>();
    for (const a of assignments) {
      if (relevantAppIds.has(a.fields.AppId)) {
        existing.set(`${a.fields.EmployeeId}:${a.fields.AppId}`, a);
      }
    }

    const unmatched: string[] = [];
    const seenKeys = new Set<string>();
    let assigned = 0;
    let revoked = 0;

    for (const seat of seats) {
      const empId = empByEmail.get(seat.email);
      if (!empId) {
        unmatched.push(seat.email);
        continue;
      }

      // Resolve target app based on tier mapping
      let targetAppId = defaultMappedAppId;
      if (config.tierMapping && seat.seatType) {
        const mapped = config.tierMapping[seat.seatType];
        if (mapped) targetAppId = mapped;
      }

      const key = `${empId}:${targetAppId}`;
      seenKeys.add(key);

      const existingAssign = existing.get(key);
      if (!existingAssign) {
        await createListItem<AppAssignment>(client, 'OC_AppAssignments', {
          EmployeeId: Number(empId),
          AppId: Number(targetAppId),
          LicenseType: seat.seatType ?? 'Standard',
          AssignedDate: seat.licensedAt ?? new Date().toISOString(),
          Status: 'Active',
          AssignedBy: `${provider} Sync`,
        });
        assigned++;
      } else if (existingAssign.fields.Status === 'Revoked') {
        await updateListItem<AppAssignment>(
          client,
          'OC_AppAssignments',
          existingAssign.id,
          { Status: 'Active', LicenseType: seat.seatType ?? 'Standard' },
        );
        assigned++;
      } else if (
        existingAssign.fields.LicenseType !== (seat.seatType ?? 'Standard')
      ) {
        // Tier changed
        await updateListItem<AppAssignment>(
          client,
          'OC_AppAssignments',
          existingAssign.id,
          { LicenseType: seat.seatType ?? 'Standard' },
        );
      }
    }

    // Revoke assignments for users no longer in source
    for (const [key, existingAssign] of existing) {
      if (!seenKeys.has(key) && existingAssign.fields.Status === 'Active') {
        await updateListItem<AppAssignment>(
          client,
          'OC_AppAssignments',
          existingAssign.id,
          { Status: 'Revoked' },
        );
        revoked++;
      }
    }

    await logChange(
      client,
      'Synced',
      'License',
      defaultMappedAppId,
      appName,
      `${provider}: ${seats.length} seats, ${assigned} assigned, ${revoked} revoked, ${unmatched.length} unmatched${config.tierMapping ? ' (multi-tier)' : ''}`,
      currentUserEmail,
    );

    return {
      provider,
      appName,
      usersFound: seats.length,
      assigned,
      revoked,
      unmatched,
    };
  } catch (err) {
    return {
      provider,
      appName,
      usersFound: 0,
      assigned: 0,
      revoked: 0,
      unmatched: [],
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

/**
 * Peek at seats without writing anything — used to populate the tier mapping UI.
 * Returns unique tier names found in the source.
 */
export async function peekIntegrationTiers(
  provider: IntegrationProvider,
  config: SyncConfig,
): Promise<{ tiers: string[]; totalSeats: number; error?: string }> {
  try {
    let seats: ExternalUserSeat[] = [];
    switch (provider) {
      case 'AzureDevOps':
        if (!config.apiKey || !config.organizationId) throw new Error('API key + org required');
        seats = await fetchAdoSeats(config.apiKey, config.organizationId);
        break;
      case 'GitHub':
        if (!config.apiKey || !config.organizationId) throw new Error('API key + org required');
        seats = await fetchGitHubCopilotSeats(config.apiKey, config.organizationId);
        break;
      case 'Figma':
        if (!config.apiKey || !config.organizationId) throw new Error('API key + team required');
        seats = await fetchFigmaSeats(config.apiKey, config.organizationId);
        break;
      case 'Slack':
        if (!config.apiKey) throw new Error('API key required');
        seats = await fetchSlackSeats(config.apiKey);
        break;
      case 'Linear':
        if (!config.apiKey) throw new Error('API key required');
        seats = await fetchLinearSeats(config.apiKey);
        break;
      case 'Claude':
        if (!config.apiKey || !config.organizationId) throw new Error('API key + org required');
        seats = await fetchClaudeSeats(config.apiKey, config.organizationId);
        break;
      case 'Snyk':
        if (!config.apiKey || !config.organizationId) throw new Error('API key + org required');
        seats = await fetchSnykSeats(config.apiKey, config.organizationId);
        break;
      case 'SendGrid':
        if (!config.apiKey) throw new Error('API key required');
        seats = await fetchSendGridSeats(config.apiKey);
        break;
      case 'CSV':
      case 'Windsurf':
      case 'Notion':
        if (!config.csvData) throw new Error('CSV data required');
        seats = parseCsvSeats(config.csvData);
        break;
    }
    const tiers = Array.from(
      new Set(seats.map((s) => s.seatType ?? 'Standard')),
    ).sort();
    return { tiers, totalSeats: seats.length };
  } catch (err) {
    return { tiers: [], totalSeats: 0, error: err instanceof Error ? err.message : String(err) };
  }
}
